import React, { useState, useEffect } from 'react';
import {
  Flex, Container, VStack, Heading, Text, FormControl, FormLabel,
  Input, Button, Card, CardBody, InputGroup, InputRightElement, Icon
} from '@chakra-ui/react';
import { LockIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import toastService from '../../utils/toastService';
import API_URL from '../../config';

const ResetPassword = ({ onRouteChange }) => {
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Get token from URL query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      toastService.error('Invalid Link', 'No reset token found.');
      onRouteChange('login');
    }
  }, [onRouteChange]);

  const handleSubmit = async () => {
    if (!newPassword || newPassword.length < 8) {
      toastService.error('Invalid Password', 'Password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      toastService.error("Passwords Don't Match", 'Please make sure passwords match.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/reset_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });

      const data = await response.json();

      if (data.success) {
        toastService.success('Password Reset', data.message);
        setTimeout(() => onRouteChange('login'), 1500);
      } else {
        toastService.error('Reset Failed', data.message);
      }
    } catch (error) {
      toastService.networkError('Request Failed', 'Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex minH="100vh" bg="gray.50">
      <Container maxW="md" py={12}>
        <VStack spacing={8}>
          <VStack spacing={4} textAlign="center">
            <Heading fontSize="3xl" fontWeight="800" bgGradient="linear(to-r, brand.600, purple.600)" bgClip="text">
              Create New Password
            </Heading>
            <Text fontSize="lg" color="gray.600">
              Enter your new password below
            </Text>
          </VStack>
          <Card shadow="xl" border="1px" borderColor="gray.200" w="full">
            <CardBody p={8}>
              <VStack spacing={6}>
                <FormControl>
                  <FormLabel>New Password</FormLabel>
                  <InputGroup>
                    <Icon as={LockIcon} color="gray.400" boxSize={5} mt={3} ml={3} position="absolute" zIndex={1} />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="At least 8 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      size="lg"
                      pl={10}
                    />
                    <InputRightElement>
                      <Button variant="ghost" size="sm" onClick={() => setShowPassword(!showPassword)} mt={2}>
                        <Icon as={showPassword ? ViewOffIcon : ViewIcon} />
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
                <FormControl>
                  <FormLabel>Confirm Password</FormLabel>
                  <InputGroup>
                    <Icon as={LockIcon} color="gray.400" boxSize={5} mt={3} ml={3} position="absolute" zIndex={1} />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Re-enter your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      size="lg"
                      pl={10}
                    />
                  </InputGroup>
                </FormControl>
                <Button
                  w="full"
                  size="lg"
                  colorScheme="brand"
                  onClick={handleSubmit}
                  isLoading={loading}
                >
                  Reset Password
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Flex>
  );
};

export default ResetPassword;
